import './styles.css';
import { elements } from './dom';
import { fetchDraftOptions, fetchDraftScript } from './features/draft-script/client';
import { createDraftScriptControls } from './features/draft-script/components/draft-script-controls';
import type { DraftEntryPath } from './features/draft-script/components/draft-script-controls';
import {
  createDraftIdeaExampleIndexCycle,
  createDraftIdeaPlaceholderCycle,
  draftIdeaExampleLabels,
  draftIdeaPlaceholders,
} from './features/draft-script/idea-placeholders';
import { formatDraftScript } from './features/draft-script/script-format';
import { createInitialDraftState } from './features/draft-script/state';
import type { DraftAnswers } from './features/draft-script/types';
import { validateGenerateRequest } from './features/draft-script/validation';
import { aspects, fallbackDefaults, qualityPresets, templates } from './features/render-reel/config';
import { createAdvancedControls } from './features/render-reel/components/advanced-controls';
import { createExportControls } from './features/render-reel/components/export-controls';
import { createImagePlacementControls } from './features/render-reel/components/image-placement-controls';
import { createMediaPicker } from './features/render-reel/components/media-picker';
import { createPreviewStage } from './features/render-reel/components/preview-stage';
import { createTitleFields } from './features/render-reel/components/title-fields';
import { buildFfmpegArgs } from './features/render-reel/encoding/ffmpeg-args';
import { cleanupVirtualFiles, loadFFmpeg } from './features/render-reel/encoding/ffmpeg-runtime';
import { initIcons } from './icons';
import { decodeWaveform, makeFallbackWaveform, readAudioDuration } from './features/render-reel/media/audio';
import { createImageObjectUrl, detectVisibleImageCrop, isImageFile } from './features/render-reel/media/image';
import { preflightAudioFile } from './features/render-reel/preflight';
import {
  beginEncodeProgress,
  endEncodeProgress,
  getFfmpegLogLines,
  hideProgressMeter,
  resetFailureDetails,
  resetFfmpegLog,
  setProgress,
  setStatus,
  showFailureDetails,
  showIndeterminateProgress,
} from './features/render-reel/progress';
import { createInitialState } from './features/render-reel/state';
import type { EncodingDefaults, Runtime } from './features/render-reel/types';
import { canvasToPng } from './utils/canvas';
import { extensionFor, slugify, stripExtension } from './utils/format';

const state = createInitialState();
const draftState = createInitialDraftState();

let audioLoadToken = 0;
let draftRequestToken = 0;
let draftOptionsAbortController: AbortController | null = null;
let draftScriptAbortController: AbortController | null = null;
let draftEntryPath: DraftEntryPath | null = null;
let homePlaceholderTransitionTimer = 0;
let homeExampleTransitionTimers: number[] = [];
let hasSetHomePlaceholder = false;
let haveSetHomeExamples = false;
const lazyWaveformDecodeMaxBytes = 32 * 1024 * 1024;
const lazyWaveformDecodeDelayMs = 350;
const draftAutomaticRetryCount = 2;

initIcons();
const mediaPicker = createMediaPicker({
  isAudioDisabled: isAudioInputDisabled,
  onAudioSelected: (file) => void loadAudio(file),
  onClearImage: clearImage,
  onFilesDropped: (files) => void loadMediaFiles(files),
  onImageSelected: (file) => void loadImage(file),
});
const titleFields = createTitleFields({
  onSubtitleInput: (subtitle) => {
    state.subtitle = subtitle;
    invalidateAndSync();
  },
  onTitleInput: (title) => {
    state.titleWasEdited = true;
    state.title = title;
    invalidateAndSync();
  },
});
const advancedControls = createAdvancedControls({
  onAspectChange: (aspect) => {
    state.aspect = aspect;
    invalidateAndSync();
  },
  onPaletteChange: (palette) => {
    state.palette = palette;
    invalidateAndSync();
  },
  onQualityChange: (quality) => {
    state.quality = quality;
    invalidateAndSync();
  },
  onTemplateChange: (template) => {
    state.template = template;
    invalidateAndSync();
  },
});
const imagePlacementControls = createImagePlacementControls({
  onFitChange: (fit) => {
    state.imagePlacement = { ...state.imagePlacement, fit };
    invalidateAndSync();
  },
  onFocalPointChange: (focalPoint) => {
    state.imagePlacement = { ...state.imagePlacement, focalPoint };
    invalidateAndSync();
  },
  onZoomChange: (zoom) => {
    state.imagePlacement = { ...state.imagePlacement, zoom };
    invalidateAndSync();
  },
});
const exportControls = createExportControls(() => void generateVideo());
const previewStage = createPreviewStage({
  hasOutput: () => Boolean(state.output),
  onPlay: () => void playGeneratedVideo(),
});
const draftScriptControls = createDraftScriptControls({
  onFindChoices: (idea, path) => void requestDraftOptions(idea, path),
  onGenerateScript: (answers) => void requestDraftScript(answers),
  onScriptInput: (script) => {
    draftState.script = script;
  },
  onReset: resetDraftScript,
  onRetryOptions: retryDraftOptions,
  onRetryScript: retryDraftScript,
});
const nextHomeIdeaPlaceholder = createDraftIdeaPlaceholderCycle();
const nextHomeExampleIndex = createDraftIdeaExampleIndexCycle();

type Workflow = 'home' | 'draft' | 'render';

const workflowPaths: Record<Workflow, string> = {
  home: '/',
  draft: '/draft-script',
  render: '/render-reel',
};

elements.homeWorkflowButton.addEventListener('click', (event) => handleWorkflowLinkClick(event, 'home'));
elements.draftWorkflowButton.addEventListener('click', (event) => handleWorkflowLinkClick(event, 'draft'));
elements.renderWorkflowButton.addEventListener('click', (event) => handleWorkflowLinkClick(event, 'render'));
elements.homeDraftIdeaForm.addEventListener('submit', (event) => {
  event.preventDefault();
  startDraftFromHome('recommendations');
});
elements.homeDraftCustomizeButton.addEventListener('click', () => startDraftFromHome('customize'));
elements.homeDraftIdeaInput.addEventListener('input', () => {
  cancelHomePlaceholderTransition();
  clearHomeIdeaValidation();
});
elements.homeDraftIdeaInput.addEventListener('focus', cancelHomePlaceholderTransition);
elements.homeDraftIdeaExampleButtons.forEach((button) => {
  button.addEventListener('click', () => {
    elements.homeDraftIdeaInput.value = button.dataset.homeDraftIdea ?? '';
    clearHomeIdeaValidation();
    startDraftFromHome('recommendations');
  });
});
setNextHomeIdeaPlaceholder();
rotateHomeExampleButtons();
window.setInterval(() => {
  if (!elements.homeDraftIdeaInput.value.trim() && document.activeElement !== elements.homeDraftIdeaInput) {
    setNextHomeIdeaPlaceholder();
  }
}, 6000);
window.setInterval(() => {
  if (!elements.homeDraftIdeaInput.value.trim() && !isHomeExampleButtonFocused()) {
    rotateHomeExampleButtons();
  }
}, 8000);
window.addEventListener('popstate', () => setWorkflow(getWorkflowFromPath(window.location.pathname)));

titleFields.setPlaceholder(state.placeholderTitle);
setWorkflow(getWorkflowFromPath(window.location.pathname));
void hydrateDefaults();
sync();

// Redraws and re-enables everything that depends on state. Every call is
// idempotent and cheap, so handlers mutate state and call this once.
function sync() {
  drawPreview();
  updateStats();
  updateGenerateAvailability();
  updateAdvancedVisibility();
}

function getWorkflowFromPath(pathname: string): Workflow {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === workflowPaths.draft) return 'draft';
  if (path === workflowPaths.render) return 'render';
  return 'home';
}

function handleWorkflowLinkClick(event: MouseEvent, workflow: Workflow) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  navigateToWorkflow(workflow);
}

function navigateToWorkflow(workflow: Workflow) {
  const path = workflowPaths[workflow];
  if (window.location.pathname !== path) {
    window.history.pushState({ workflow }, '', path);
  }
  setWorkflow(workflow);
}

function startDraftFromHome(path: DraftEntryPath) {
  const idea = elements.homeDraftIdeaInput.value.trim();
  if (!idea) {
    elements.homeDraftIdeaValidation.textContent = 'Add a podcast topic to continue.';
    elements.homeDraftIdeaValidation.hidden = false;
    elements.homeDraftIdeaInput.focus();
    return;
  }

  clearHomeIdeaValidation();
  navigateToWorkflow('draft');
  void requestDraftOptions(idea, path);
}

function clearHomeIdeaValidation() {
  elements.homeDraftIdeaValidation.hidden = true;
  elements.homeDraftIdeaValidation.textContent = '';
}

function setNextHomeIdeaPlaceholder() {
  const input = elements.homeDraftIdeaInput;
  const placeholder = nextHomeIdeaPlaceholder();
  cancelHomePlaceholderTransition();

  if (!hasSetHomePlaceholder || prefersReducedMotion()) {
    input.placeholder = placeholder;
    hasSetHomePlaceholder = true;
    return;
  }

  input.classList.add('is-placeholder-fading');
  homePlaceholderTransitionTimer = window.setTimeout(() => {
    input.placeholder = placeholder;
    input.classList.remove('is-placeholder-fading');
    void input.offsetWidth;
    input.classList.add('is-placeholder-entering');
    homePlaceholderTransitionTimer = window.setTimeout(() => {
      input.classList.remove('is-placeholder-entering');
      homePlaceholderTransitionTimer = 0;
    }, 180);
  }, 140);
}

function rotateHomeExampleButtons() {
  const nextExamples = elements.homeDraftIdeaExampleButtons.map((button) => {
    const exampleIndex = nextHomeExampleIndex();
    return exampleIndex === null ? null : { button, exampleIndex };
  });

  cancelHomeExampleTransitions();
  nextExamples.forEach((example, index) => {
    if (!example) return;
    if (!haveSetHomeExamples || prefersReducedMotion()) {
      setHomeExample(example.button, example.exampleIndex);
      return;
    }
    homeExampleTransitionTimers.push(window.setTimeout(() => {
      transitionHomeExample(example.button, example.exampleIndex);
    }, index * 80));
  });
  haveSetHomeExamples = true;
}

function cancelHomePlaceholderTransition() {
  window.clearTimeout(homePlaceholderTransitionTimer);
  homePlaceholderTransitionTimer = 0;
  elements.homeDraftIdeaInput.classList.remove('is-placeholder-fading', 'is-placeholder-entering');
}

function cancelHomeExampleTransitions() {
  homeExampleTransitionTimers.forEach((timer) => window.clearTimeout(timer));
  homeExampleTransitionTimers = [];
  elements.homeDraftIdeaExampleButtons.forEach((button) => {
    button.querySelector('.draft-example-label')?.classList.remove('is-fading-out', 'is-fading-in');
  });
}

function transitionHomeExample(button: HTMLButtonElement, exampleIndex: number) {
  const label = button.querySelector<HTMLElement>('.draft-example-label');
  if (!label) return;
  label.classList.add('is-fading-out');
  homeExampleTransitionTimers.push(window.setTimeout(() => {
    setHomeExample(button, exampleIndex);
    label.classList.remove('is-fading-out');
    void label.offsetWidth;
    label.classList.add('is-fading-in');
    homeExampleTransitionTimers.push(window.setTimeout(() => label.classList.remove('is-fading-in'), 180));
  }, 140));
}

function setHomeExample(button: HTMLButtonElement, exampleIndex: number) {
  const label = draftIdeaExampleLabels[exampleIndex];
  button.dataset.homeDraftIdea = draftIdeaPlaceholders[exampleIndex];
  button.querySelector<HTMLElement>('.draft-example-label')!.textContent = label;
  button.setAttribute('aria-label', `Use the ${label} example`);
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isHomeExampleButtonFocused() {
  return elements.homeDraftIdeaExampleButtons.some((button) => button === document.activeElement);
}

function setWorkflow(workflow: Workflow) {
  elements.homeWorkspace.hidden = workflow !== 'home';
  elements.draftScriptWorkspace.hidden = workflow !== 'draft';
  elements.renderReelWorkspace.hidden = workflow !== 'render';

  const isHome = workflow === 'home';
  document.body.classList.toggle('is-home-workflow', isHome);
  const isDraft = workflow === 'draft';
  elements.homeWorkflowButton.classList.toggle('active', isHome);
  setCurrentPage(elements.homeWorkflowButton, isHome);
  elements.draftWorkflowButton.classList.toggle('active', isDraft);
  setCurrentPage(elements.draftWorkflowButton, isDraft);
  const isRender = workflow === 'render';
  elements.renderWorkflowButton.classList.toggle('active', isRender);
  setCurrentPage(elements.renderWorkflowButton, isRender);
  document.title = isHome
    ? 'PodCrucible | Ideas become scripts. Sounds become scenes.'
    : isDraft
      ? 'Draft Script | PodCrucible'
      : 'Render Reel | PodCrucible';
}

function setCurrentPage(link: HTMLAnchorElement, isCurrent: boolean) {
  if (isCurrent) {
    link.setAttribute('aria-current', 'page');
  } else {
    link.removeAttribute('aria-current');
  }
}

async function requestDraftOptions(idea: string, path: DraftEntryPath) {
  draftOptionsAbortController?.abort();
  draftScriptAbortController?.abort();
  const controller = new AbortController();
  draftOptionsAbortController = controller;
  const requestToken = ++draftRequestToken;
  draftEntryPath = path;

  Object.assign(draftState, createInitialDraftState(), { idea, status: 'loading-options' });
  draftScriptControls.reset();
  draftScriptControls.setIdea(idea);
  draftScriptControls.setOptionsLoading(true, path);

  try {
    const result = await requestWithAutomaticRetries(
      () => fetchDraftOptions(idea, controller.signal),
      controller.signal,
      (nextAttempt, totalAttempts) => draftScriptControls.setOptionsRetryStatus(nextAttempt, totalAttempts),
    );
    if (requestToken !== draftRequestToken) return;
    draftState.choices = result.choices;
    draftState.status = path === 'customize' ? 'answering' : 'generating-script';
    draftScriptControls.setOptions(result.choices, path === 'customize');
    if (path === 'recommendations') {
      draftScriptControls.setOptionsLoading(false, path);
      await requestDraftScript(draftScriptControls.getAnswers());
    }
  } catch (error) {
    if (isAbortError(error) || requestToken !== draftRequestToken) return;
    draftState.status = 'error';
    draftState.error = getDraftErrorMessage(error);
    draftScriptControls.setOptionsError('Please try again in a moment. We made a couple of attempts automatically.');
  } finally {
    if (requestToken === draftRequestToken) {
      draftScriptControls.setOptionsLoading(false);
    }
  }
}

async function requestDraftScript(answers: DraftAnswers) {
  let request: ReturnType<typeof validateGenerateRequest>;
  try {
    request = validateGenerateRequest({ idea: draftState.idea, choices: draftState.choices, answers });
  } catch (error) {
    draftState.status = 'error';
    draftState.error = getDraftErrorMessage(error);
    draftScriptControls.setScriptError(draftState.error, false);
    return;
  }

  draftOptionsAbortController?.abort();
  draftScriptAbortController?.abort();
  const controller = new AbortController();
  draftScriptAbortController = controller;
  const requestToken = ++draftRequestToken;
  draftState.answers = request.answers;
  draftState.status = 'generating-script';
  draftState.error = '';
  draftScriptControls.setScriptLoading(true);

  try {
    const result = await requestWithAutomaticRetries(
      () => fetchDraftScript(request, controller.signal),
      controller.signal,
    );
    if (requestToken !== draftRequestToken) return;
    const formattedScript = formatDraftScript(result.script, result.title, result.logline);
    draftState.title = result.title;
    draftState.script = formattedScript;
    draftState.status = 'ready';
    draftScriptControls.setScript(result.title, formattedScript);
  } catch (error) {
    if (isAbortError(error) || requestToken !== draftRequestToken) return;
    draftState.status = 'error';
    draftState.error = getDraftErrorMessage(error);
    draftScriptControls.setScriptError(getManualRetryMessage(draftState.error));
  } finally {
    if (requestToken === draftRequestToken) {
      draftScriptControls.setScriptLoading(false);
    }
  }
}

function resetDraftScript() {
  draftOptionsAbortController?.abort();
  draftScriptAbortController?.abort();
  draftRequestToken += 1;
  draftEntryPath = null;
  Object.assign(draftState, createInitialDraftState());
  draftScriptControls.reset();
}

function retryDraftOptions() {
  if (!draftState.idea || !draftEntryPath) return;
  void requestDraftOptions(draftState.idea, draftEntryPath);
}

function retryDraftScript() {
  if (draftState.choices.length === 0) return;
  void requestDraftScript(draftScriptControls.getAnswers());
}

async function requestWithAutomaticRetries<T>(
  request: () => Promise<T>,
  signal: AbortSignal,
  onRetry?: (nextAttempt: number, totalAttempts: number) => void,
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= draftAutomaticRetryCount; attempt += 1) {
    try {
      return await request();
    } catch (error) {
      lastError = error;
      if (isAbortError(error) || attempt === draftAutomaticRetryCount) {
        throw error;
      }
      onRetry?.(attempt + 2, draftAutomaticRetryCount + 1);
      await waitForDraftRetry(signal, (attempt + 1) * 600);
    }
  }
  throw lastError;
}

function waitForDraftRetry(signal: AbortSignal, delayMs: number) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('The request was cancelled.', 'AbortError'));
      return;
    }

    let timer = 0;
    const abort = () => {
      window.clearTimeout(timer);
      reject(new DOMException('The request was cancelled.', 'AbortError'));
    };
    timer = window.setTimeout(() => {
      signal.removeEventListener('abort', abort);
      resolve();
    }, delayMs);
    signal.addEventListener('abort', abort, { once: true });
  });
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError';
}

function getDraftErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'The draft service failed. Try again.';
}

function getManualRetryMessage(message: string) {
  return `${message} We tried twice automatically.`;
}

// For handlers whose state edit invalidates any generated video: drop the
// stale output, then re-sync the UI.
function invalidateAndSync() {
  revokeOutput();
  sync();
}

async function loadMediaFiles(files: FileList | File[]) {
  const selectedFiles = Array.from(files);
  const audioFile = selectedFiles.find((file) => file.type.startsWith('audio/'));
  const imageFile = selectedFiles.find(isImageFile);

  if (!audioFile && !imageFile) {
    setStatus('failed');
    showFailureDetails('Drop audio or image files.');
    return;
  }

  if (audioFile) {
    await loadAudio(audioFile);
  }
  if (imageFile) {
    await loadImage(imageFile);
  }
}

async function hydrateDefaults() {
  if (!import.meta.env.DEV) {
    try {
      const response = await fetch('/api/encoding-defaults', {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`Defaults endpoint returned ${response.status}`);
      }
      const defaults = (await response.json()) as Partial<EncodingDefaults>;
      state.quality = defaults.quality && defaults.quality in qualityPresets ? defaults.quality : fallbackDefaults.quality;
      state.aspect = defaults.aspect && defaults.aspect in aspects ? defaults.aspect : fallbackDefaults.aspect;
      state.template = defaults.template && defaults.template in templates ? defaults.template : fallbackDefaults.template;
    } catch {
      // Local defaults are already applied in state.
    }
  }

  advancedControls.setActiveAspect(state.aspect);
  advancedControls.setActiveQuality(state.quality);
  advancedControls.setActiveTemplate(state.template);
  imagePlacementControls.setPlacement(state.imagePlacement);
  sync();
}

async function loadAudio(file: File) {
  if (isAudioInputDisabled()) {
    return;
  }

  const preflight = preflightAudioFile(file);
  if (!preflight.ok) {
    rejectAudioFile(preflight.message);
    return;
  }

  const loadToken = ++audioLoadToken;
  revokeOutput();
  state.audioFile = file;
  state.audioMeta = null;
  state.isReadingAudio = true;
  if (!state.titleWasEdited) {
    state.title = stripExtension(file.name);
    titleFields.setTitle(state.title);
  }
  mediaPicker.setAudioFile(file);
  resetFailureDetails();
  setStatus('reading');
  showIndeterminateProgress();
  sync();

  let shouldDecodeWaveform = false;
  try {
    const duration = await readDurationOrZero(file);
    if (loadToken !== audioLoadToken) {
      return;
    }

    state.audioMeta = {
      duration,
      waveform: makeFallbackWaveform(96),
    };
    shouldDecodeWaveform = shouldDecodeWaveformLazily(file);
    resetFailureDetails();
    sync();
  } finally {
    if (loadToken === audioLoadToken) {
      state.isReadingAudio = false;
      setReadyStatusIfIdle();
      hideProgressMeter();
      sync();
    }
  }

  if (loadToken === audioLoadToken && shouldDecodeWaveform) {
    scheduleLazyWaveformDecode(file, loadToken);
  }
}

async function readDurationOrZero(file: File) {
  try {
    return await readAudioDuration(file);
  } catch {
    return 0;
  }
}

function shouldDecodeWaveformLazily(file: File) {
  return file.size <= lazyWaveformDecodeMaxBytes;
}

function scheduleLazyWaveformDecode(file: File, loadToken: number) {
  window.setTimeout(() => {
    if (loadToken !== audioLoadToken || state.audioFile !== file || !state.audioMeta || state.isBusy || state.output) {
      return;
    }

    void decodeWaveform(file)
      .then((waveform) => {
        if (loadToken !== audioLoadToken || state.audioFile !== file || !state.audioMeta) {
          return;
        }

        state.audioMeta = {
          ...state.audioMeta,
          waveform,
        };

        if (!state.output) {
          drawPreview();
        }
      })
      .catch(() => {
        // The fallback waveform is already in place.
      });
  }, lazyWaveformDecodeDelayMs);
}

async function loadImage(file: File) {
  if (!isImageFile(file)) {
    setStatus('failed');
    showFailureDetails('Select an image file.');
    return;
  }

  let objectUrl: string | null = null;
  const image = new Image();

  try {
    objectUrl = await createImageObjectUrl(file);
    const imageUrl = objectUrl;
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Could not read image'));
      image.src = imageUrl;
    });
  } catch (error) {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
    setStatus('failed');
    showFailureDetails(error instanceof Error ? error.message : 'Image preview unavailable.');
    return;
  }

  const crop = detectVisibleImageCrop(image);
  revokeOutput();
  revokeUploadedImage();
  state.image = { element: image, file, objectUrl, crop };
  mediaPicker.setImageFile(file);
  setReadyStatusIfIdle();
  sync();
}

function rejectAudioFile(message: string) {
  audioLoadToken += 1;
  revokeOutput();
  state.audioFile = null;
  state.audioMeta = null;
  state.isReadingAudio = false;
  mediaPicker.resetAudio();
  hideProgressMeter();
  sync();
  setStatus('failed');
  showFailureDetails(message);
}

function drawPreview() {
  previewStage.draw({
    aspect: state.aspect,
    audioMeta: state.audioMeta,
    image: state.image,
    imagePlacement: state.imagePlacement,
    palette: state.palette,
    subtitle: state.subtitle,
    template: state.template,
    title: currentTitle(),
  });
}

async function generateVideo() {
  if (!state.audioFile) {
    setStatus('failed');
    hideProgressMeter();
    showFailureDetails('Select an audio file before generating a video.');
    return;
  }

  const preflight = preflightAudioFile(state.audioFile);
  if (!preflight.ok) {
    rejectAudioFile(preflight.message);
    return;
  }

  advancedControls.collapse();
  setBusy(true);
  resetFailureDetails();
  resetFfmpegLog();
  invalidateAndSync();
  setProgress(0.03);
  setStatus('encoding');

  const inputName = `input.${extensionFor(state.audioFile.name)}`;
  const frameName = 'title-card.png';
  const outputName = 'output.mp4';
  let runtime: Runtime | null = null;

  try {
    const [loadedRuntime, frameBlob] = await Promise.all([loadFFmpeg(), canvasToPng(previewStage.getCanvas())]);
    runtime = loadedRuntime;
    const { ffmpeg, fetchFile: readFile } = loadedRuntime;

    setProgress(0.14);
    await cleanupVirtualFiles(ffmpeg, [inputName, frameName, outputName]);
    await ffmpeg.writeFile(inputName, await readFile(state.audioFile));
    await ffmpeg.writeFile(frameName, await readFile(frameBlob));

    beginEncodeProgress(state.audioMeta?.duration ?? 0);
    await ffmpeg.exec(
      buildFfmpegArgs({
        frameName,
        inputName,
        outputName,
        quality: state.quality,
        title: currentTitle(),
      }),
    );
    setProgress(0.96);

    const data = await ffmpeg.readFile(outputName);
    // readFile copies the data out of the worker, so the bytes are backed by a
    // plain ArrayBuffer despite the ArrayBufferLike typing; no extra copy needed.
    const blobPart = typeof data === 'string' ? data : (data as Uint8Array<ArrayBuffer>);
    const blob = new Blob([blobPart], { type: 'video/mp4' });
    const filename = `${slugify(currentTitle())}-${state.quality}.mp4`;
    const url = URL.createObjectURL(blob);
    state.output = { blob, url };

    updateStats();
    previewStage.showStats();
    exportControls.showDownload(url, filename);
    previewStage.showOutput(url);
    setProgress(1);
    setStatus('done');
    hideProgressMeter();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const ffmpegLogLines = getFfmpegLogLines();
    setStatus('failed');
    hideProgressMeter();
    showFailureDetails(ffmpegLogLines.length ? `${message}\n\nFFmpeg log:\n${ffmpegLogLines.join('\n')}` : message);
  } finally {
    if (runtime) {
      await cleanupVirtualFiles(runtime.ffmpeg, [inputName, frameName, outputName]);
    }
    endEncodeProgress();
    setBusy(false);
  }
}

function revokeOutput() {
  if (state.output) {
    URL.revokeObjectURL(state.output.url);
    state.output = null;
    exportControls.resetDownload();
    previewStage.resetOutput();
  }
  resetFailureDetails();
  if (!state.isBusy && !state.isReadingAudio) {
    setStatus('ready');
    setProgress(0);
    hideProgressMeter();
  }
}

function clearImage() {
  revokeOutput();
  revokeUploadedImage();
  mediaPicker.resetImage();
  setReadyStatusIfIdle();
  sync();
}

function revokeUploadedImage() {
  if (state.image) {
    URL.revokeObjectURL(state.image.objectUrl);
  }
  state.image = null;
}

async function playGeneratedVideo() {
  if (!state.output) {
    return;
  }
  await previewStage.playVideo();
}

function setBusy(isBusy: boolean) {
  state.isBusy = isBusy;
  updateGenerateAvailability();
}

function updateGenerateAvailability() {
  const isLoading = state.isBusy || state.isReadingAudio;
  const setupDisabled = state.isBusy;
  mediaPicker.setSetupDisabled(setupDisabled);
  mediaPicker.setAudioDisabled(isLoading);
  titleFields.setSetupDisabled(setupDisabled);
  advancedControls.setSetupDisabled(setupDisabled);
  imagePlacementControls.setSetupDisabled(setupDisabled);
  exportControls.updateGenerateButton({
    hasAudioFile: Boolean(state.audioFile),
    hasAudioMeta: Boolean(state.audioMeta),
    hasGeneratedOutput: Boolean(state.output),
    isBusy: state.isBusy,
    isReadingAudio: state.isReadingAudio,
  });
}

function isAudioInputDisabled() {
  return state.isBusy || state.isReadingAudio;
}

function updateAdvancedVisibility() {
  advancedControls.updateVisibility(Boolean(state.audioFile));
  imagePlacementControls.updateVisibility(Boolean(state.image));
}

function setReadyStatusIfIdle() {
  if (!state.isBusy && !state.isReadingAudio) {
    setStatus('ready');
  }
}

function updateStats() {
  previewStage.updateStats({
    audioFile: state.audioFile,
    duration: state.audioMeta?.duration ?? 0,
    outputBlob: state.output?.blob ?? null,
    qualitySummary: qualityPresets[state.quality].description,
  });
}

function currentTitle() {
  return state.title || state.placeholderTitle;
}
